/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react';
import styled from 'styled-components';
import { ParagraphPlugin } from './ParagraphPlugin';
import { RendererWithPlugins } from './RendererWithPlugins';

export interface EditablePluginsProps
  extends Omit<React.TextareaHTMLAttributes<HTMLDivElement>, 'onChange' | 'onKeyDown' | 'value'> {
  value?: any;
  as?: React.ElementType;
  /**
   * Each plugin fields will be combined by role.
   *
   * To render `Editable`:
   * - decorate
   * - renderElement
   * - renderLeaf
   * - onDOMBeforeInput
   * - onKeyDown
   */
  plugins?: any[];
  /**
   * Decorations are another type of text-level formatting.
   * They are similar to regular old custom properties,
   * except each one applies to a Range of the document instead of being
   * associated with a given text node.
   * However, decorations are computed at render-time based on the content itself.
   * This is helpful for dynamic formatting like syntax highlighting or search
   * keywords, where changes to the content (or some external data) has the
   * potential to change the formatting.
   * Me oi, con nho me
   */
  decorate?: any[];
  // Dependencies of `decorate`
  decorateDeps?: any[];
  /**
   * To customize the rendering of each element components.
   * Element properties are for contiguous, semantic elements in the document.
   */
  renderElement?: any[];
  // Dependencies of `renderElement`
  renderElementDeps?: any[];
  /**
   * To customize the rendering of each leaf.
   * When text-level formatting is rendered, the characters are grouped into
   * "leaves" of text that each contain the same formatting applied to them.
   * Text properties are for non-contiguous, character-level formatting.
   */
  renderLeaf?: any[];
  // Dependencies of `renderLeaf`
  renderLeafDeps?: any[];
  onDOMBeforeInput?: any[];
  // Dependencies of `onDOMBeforeInput`
  onDOMBeforeInputDeps?: any[];
  /**
   * Handlers when we press a key
   */
  onKeyDown?: any[];
  // Dependencies of `onKeyDown`
  onKeyDownDeps?: any[];

  [key: string]: any;
}

// render logic
// input: [{}, {}]
// each object can be
/*
  - Text
  => if no mark => render text directly
  => if mark => handled by renderLeaf already
  - Element
  => each element already have renderElement fn
  => customize render paragraph
  => render paragraph should render p
 */
// output:
/*
  <>
    {
      input.map(it => {
        if (it is element) {
          element.children = element.children.map(renderElement);
          render element
        }
      })
    }
    {}
  </>
 */

const ReadOnlyWrapper = styled.div`
  > p {
    margin-top: 24px;
    white-space: pre-wrap;
    &:first-child {
      margin-top: 0;
    }
  }
`;

export const ReadOnlyPlugins = ({
  plugins = [] as any,
  decorate: decorateList = [],
  decorateDeps = [],
  renderElement: renderElementList = [],
  renderElementDeps = [],
  renderLeaf: renderLeafList = [],
  renderLeafDeps = [],
  onDOMBeforeInput: onDOMBeforeInputList = [],
  onDOMBeforeInputDeps = [],
  onKeyDown: onKeyDownList = [],
  onKeyDownDeps = [],
  value = [],
  as, // Destructure 'as' so it's not passed down
  ...otherProps
}: EditablePluginsProps) => {
  const rendererPlugins = React.useMemo(
    () => [
      ParagraphPlugin,
      ...plugins.filter((plugin) => plugin.renderElement || plugin.renderLeaf),
    ],
    [plugins]
  );

  // Use otherProps instead of props to avoid passing 'as'
  return (
    <ReadOnlyWrapper {...otherProps}>
      <RendererWithPlugins
        plugins={rendererPlugins}
        renderLeaf={renderLeafList}
        renderElement={renderElementList}
        value={value}
      />
    </ReadOnlyWrapper>
  );
};
